import { Box, Button, Switch, Text, Title } from "@mantine/core";
import type { NumberInputProps } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "../../components/permission";
import classes from "./settings-ui.module.css";

export type SettingsTabProps = {
  /** Баруун aside баганын DOM зангилаа — таб өөрийн хураангуйг portal-оор энд буулгана. */
  asideTarget: HTMLElement | null;
  /** Хадгалаагүй өөрчлөлттэй талбарын тоог хуудас руу мэдэгдэнэ. */
  onDirtyChange: (count: number) => void;
};

const sameValue = (a: unknown, b: unknown) => {
  if (a === b) return true;
  // Сервер DECIMAL-ийг "0.0500" гэж өгдөг, NumberInput тоо буцаадаг — утгаар нь жишнэ.
  if ((typeof a === "number" || typeof b === "number") && a !== "" && b !== "" && a != null && b != null) {
    return Number(a) === Number(b);
  }
  return JSON.stringify(a) === JSON.stringify(b);
};

/** Хадгалсан хувилбараас ялгаатай талбарын тоо. */
export const countChanges = <T extends object>(current: T, saved: T) =>
  (Object.keys(current) as (keyof T)[]).filter((key) => !sameValue(current[key], saved[key])).length;

/** NumberInput-ийн баруун талд нэгж (% · хоног · ₮) харуулах props. */
export const unitProps = (unit: string): Partial<NumberInputProps> => ({
  rightSection: (
    <Text span className={classes.unit}>
      {unit}
    </Text>
  ),
  rightSectionWidth: unit.length <= 1 ? 36 : unit.length * 9 + 20,
  rightSectionPointerEvents: "none",
  hideControls: true,
});

type MasterProps = {
  title: string;
  subtitle: string;
  checked: boolean;
  onChange: (v: boolean) => void;
};

/** Асаалттай үеийн үндсэн карт — «Асаалттай» текст ба switch баруун талд. */
export function MasterCard({ title, subtitle, checked, onChange }: MasterProps) {
  const { t } = useTranslation("settings");
  return (
    <Box component="section" className={`${classes.card} ${classes.masterCard}`}>
      <Box className={classes.heading}>
        <Text component="span" className={classes.masterTitle}>{title}</Text>
        <Text component="span" className={classes.masterSub}>{subtitle}</Text>
      </Box>
      <Box className={classes.masterSwitch}>
        <Text component="span" className={classes.masterState}>{t("status.on")}</Text>
        <Switch aria-label={title} checked={checked} onChange={(e) => onChange(e.currentTarget.checked)} />
      </Box>
    </Box>
  );
}

type OffCardProps = {
  title: string;
  description: string;
  enableLabel: string;
  onEnable: () => void;
  features: { title: string; description: string }[];
};

/** Унтраалттай үеийн ганц карт — маягтын оронд гарна. */
export function OffCard({ title, description, enableLabel, onEnable, features }: OffCardProps) {
  const canFinance = usePermission("FINANCE").hasAccess;
  return (
    <Box component="section" className={`${classes.card} ${classes.offCard}`}>
      <Box className={classes.offHead}>
        <Box className={classes.heading}>
          <Text component="span" className={classes.offTitle}>{title}</Text>
          <Text component="span" className={classes.offDescription}>{description}</Text>
        </Box>
        {canFinance && <Button onClick={onEnable} className={classes.offAction}>{enableLabel}</Button>}
      </Box>
      <Box className={classes.features}>
        {features.map((f) => (
          <Box key={f.title} className={classes.feature}>
            <Text component="span" className={classes.featureTitle}>{f.title}</Text>
            <Text component="span" className={classes.featureText}>{f.description}</Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

type SectionProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

/** Карт: толгой (h2 + тайлбар), доор нь мөрүүд. */
export function SectionCard({ title, subtitle, children }: SectionProps) {
  return (
    <Box component="section" className={`${classes.card} ${classes.sectionCard}`}>
      <Box className={classes.cardHead}>
        <Title order={2} className={classes.sectionTitle}>{title}</Title>
        {subtitle && <Text component="span" className={classes.sub}>{subtitle}</Text>}
      </Box>
      {children}
    </Box>
  );
}

type RowProps = {
  label: string;
  description?: string;
  required?: boolean;
  /** Control-ийг мөрийн доор бүтэн өргөнөөр байрлуулна (Textarea г.м.). */
  stacked?: boolean;
  isSwitch?: boolean;
  children: ReactNode;
};

/** Мөр: зүүнд нэр + тайлбар, баруунд control (260px). Тайлбар хэдэн мөр ч control нэг шугамд. */
export function SettingRow({ label, description, required, stacked, isSwitch, children }: RowProps) {
  return (
    <Box className={classes.row} data-stacked={stacked || undefined}>
      <Box className={classes.heading}>
        <Text component="span" className={classes.rowLabel}>
          {label}
          {required && <Text span className={classes.required}> *</Text>}
        </Text>
        {description && <Text component="span" className={classes.sub}>{description}</Text>}
      </Box>
      <Box className={classes.rowControl} data-switch={isSwitch || undefined}>{children}</Box>
    </Box>
  );
}

type SwitchRowProps = {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
};

export function SwitchRow({ label, description, checked, onChange, disabled }: SwitchRowProps) {
  return (
    <SettingRow label={label} description={description} isSwitch>
      <Switch aria-label={label} checked={checked} onChange={(e) => onChange(e.currentTarget.checked)} disabled={disabled} />
    </SettingRow>
  );
}

type SaveBarProps = {
  /** Хадгалсан хувилбараас ялгаатай талбарын тоо; 0 бол мөр харагдахгүй. */
  count: number;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
};

/** Зөвхөн dirty үед: тохиргооны баганын доор хөвдөг бараан мөр. */
export function SaveBar({ count, saving, onSave, onCancel }: SaveBarProps) {
  const { t } = useTranslation("settings");
  const canFinance = usePermission("FINANCE").hasAccess;
  if (count === 0 || !canFinance) return null;
  return (
    <Box className={classes.saveBar} role="status">
      <Text component="span" className={classes.saveText}>
        <Text span className={classes.saveCount}>{t("unsaved.count", { count })}</Text> {t("unsaved.suffix")}
      </Text>
      <Box className={classes.saveActions}>
        <Button variant="subtle" color="gray" className={classes.saveCancel} onClick={onCancel} disabled={saving}>
          {t("action.cancel")}
        </Button>
        <Button onClick={onSave} loading={saving}>
          {t("action.save")}
        </Button>
      </Box>
    </Box>
  );
}

export const settingsClasses = classes;
